/**
 * Access console → "Chatbot usage" tab.
 *
 * What every account has spent on the analyzer chat (Ask): questions,
 * conversations, tokens, cost (USD as Anthropic bills it, ₹ at a display
 * rate) and how readers rated the answers. Admin (superuser) accounts are
 * listed apart from customers, and your own usage sits on top, so the
 * team's testing never blurs what customers run up.
 *
 * Click a row for that account's day-by-day spend and every question it
 * asked, with what each answer cost.
 *
 * Backend: GET /api/admin/v2/ask-usage/  and  /ask-usage/<user_id>/
 */
import { useEffect, useState } from "react";
import {
  Bot, RefreshCw, X, ThumbsUp, ThumbsDown, ShieldCheck, Users as UsersIcon, AlertCircle,
} from "lucide-react";
import { getAskUsage, getAskUsageUser } from "../../api/owner.api";

const PERIODS = [
  { key: "month", label: "This month" },
  { key: "30d", label: "Last 30 days" },
  { key: "all", label: "All time" },
];

const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const inr = (c, rate) => `₹${(((c || 0) / 100) * (rate || 88)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const num = (n) => (n || 0).toLocaleString("en-IN");
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function AskUsageSection() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openUser, setOpenUser] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await getAskUsage(period);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Could not load usage.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [period]);

  const rate = data?.inr_per_usd;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Bot className="w-6 h-6 text-indigo-400" /> Chatbot usage
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            What each account has spent on the analyzer chat. Costs are what Anthropic bills (USD);
            ₹ uses a display rate of ₹{rate || 88} per $.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border-default overflow-hidden">
            {PERIODS.map((p) => (
              <button key={p.key} type="button" onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 text-sm ${period === p.key ? "bg-button-primary text-white" : "text-text-secondary hover:text-text-primary hover:bg-bg-secondary"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={load} title="Refresh"
            className="p-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label={`Total spend · ${data.period.label}`} value={usd(data.totals.cost_cents)}
              sub={inr(data.totals.cost_cents, rate)} />
            <Stat label="Customers' spend" value={usd(data.customer_totals.cost_cents)}
              sub={`${num(data.customer_totals.users)} accounts · ${num(data.customer_totals.questions)} questions`} />
            <Stat label="Admin accounts' spend" value={usd(data.admin_totals.cost_cents)}
              sub={`${num(data.admin_totals.users)} accounts · ${num(data.admin_totals.questions)} questions`} />
            <Stat label="Answers rated" value={`${num(data.totals.thumbs_up)} 👍 · ${num(data.totals.thumbs_down)} 👎`}
              sub={`${num(data.totals.questions)} questions in all`} />
          </div>

          <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/[0.06] p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-text-primary mb-2">
              <ShieldCheck className="w-4 h-4 text-indigo-300" /> Your usage ({data.you.email})
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <Inline label="Spend" value={`${usd(data.you.cost_cents)} · ${inr(data.you.cost_cents, rate)}`} />
              <Inline label="Questions" value={num(data.you.questions)} />
              <Inline label="Conversations" value={num(data.you.conversations)} />
              <Inline label="Avg per question" value={data.you.avg_cost_cents != null ? usd(data.you.avg_cost_cents) : "—"} />
              <Inline label="Last used" value={when(data.you.last_used)} />
              {data.you.questions > 0 && (
                <button type="button" onClick={() => setOpenUser(data.you)}
                  className="text-indigo-300 hover:text-indigo-200 underline decoration-dotted">See every question</button>
              )}
            </div>
          </div>

          <UsageTable title="Admin accounts" icon={<ShieldCheck className="w-4 h-4" />}
            rows={data.admins} rate={rate} onOpen={setOpenUser}
            empty="No admin account has used the chat in this period." />
          <UsageTable title="Customers" icon={<UsersIcon className="w-4 h-4" />}
            rows={data.users} rate={rate} onOpen={setOpenUser}
            empty="No customer has used the chat in this period." />
        </>
      )}

      {openUser && (
        <UserDrawer user={openUser} period={period} rate={rate} onClose={() => setOpenUser(null)} />
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-border-default bg-bg-secondary p-4">
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className="text-2xl font-bold text-text-primary mt-1">{value}</div>
      {sub && <div className="text-xs text-text-secondary mt-0.5">{sub}</div>}
    </div>
  );
}

function Inline({ label, value }) {
  return (
    <div>
      <span className="text-text-muted">{label}: </span>
      <span className="text-text-primary font-medium">{value}</span>
    </div>
  );
}

function UsageTable({ title, icon, rows, rate, onOpen, empty }) {
  return (
    <div className="rounded-xl border border-border-default bg-bg-secondary overflow-hidden">
      <div className="px-4 py-3 border-b border-border-default flex items-center gap-2 text-sm font-semibold text-text-primary">
        {icon} {title} <span className="text-text-muted font-normal">· {rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-text-muted">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-primary/40 text-text-secondary text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Account</th>
                <th className="text-right px-4 py-2.5 font-semibold">Questions</th>
                <th className="text-right px-4 py-2.5 font-semibold">Chats</th>
                <th className="text-right px-4 py-2.5 font-semibold">Spend</th>
                <th className="text-right px-4 py-2.5 font-semibold">≈ ₹</th>
                <th className="text-right px-4 py-2.5 font-semibold">Per question</th>
                <th className="text-right px-4 py-2.5 font-semibold">Tokens in / out</th>
                <th className="text-center px-4 py-2.5 font-semibold">Ratings</th>
                <th className="text-left px-4 py-2.5 font-semibold">Last used</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {rows.map((r) => (
                <tr key={r.user_id} onClick={() => onOpen(r)}
                  className="cursor-pointer hover:bg-bg-primary/40">
                  <td className="px-4 py-2.5">
                    <div className="text-text-primary font-medium">{r.name}</div>
                    <div className="text-xs text-text-muted">{r.email}{r.organization ? ` · ${r.organization}` : ""}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{num(r.questions)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{num(r.conversations)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-text-primary">{usd(r.cost_cents)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-text-secondary">{inr(r.cost_cents, rate)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-text-secondary">{r.avg_cost_cents != null ? usd(r.avg_cost_cents) : "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-text-secondary">{num(r.input_tokens)} / {num(r.output_tokens)}</td>
                  <td className="px-4 py-2.5 text-center text-text-secondary whitespace-nowrap">
                    <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5 text-emerald-400" />{r.thumbs_up}</span>
                    <span className="inline-flex items-center gap-1 ml-3"><ThumbsDown className="w-3.5 h-3.5 text-rose-400" />{r.thumbs_down}</span>
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary whitespace-nowrap">{when(r.last_used)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UserDrawer({ user, period, rate, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    getAskUsageUser(user.user_id, period)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.data?.detail || e.message));
    return () => { alive = false; };
  }, [user.user_id, period]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <aside className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[640px] bg-bg-primary border-l border-border-default shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-border-default flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-text-muted">{user.is_admin ? "Admin account" : "Customer"}</div>
            <div className="text-lg font-semibold text-text-primary">{user.name}</div>
            <div className="text-sm text-text-secondary">{user.email}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-text-secondary hover:text-text-primary"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {error && <div className="text-sm text-red-300">{error}</div>}
          {!data && !error && <div className="text-sm text-text-muted">Loading…</div>}
          {data && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Stat label={`Spend · ${data.period.label}`} value={usd(data.row.cost_cents)} sub={inr(data.row.cost_cents, rate)} />
                <Stat label="Questions" value={num(data.row.questions)} sub={`${num(data.row.conversations)} conversations`} />
                <Stat label="Per question" value={data.row.avg_cost_cents != null ? usd(data.row.avg_cost_cents) : "—"}
                  sub={`${data.row.thumbs_up} 👍 · ${data.row.thumbs_down} 👎`} />
              </div>

              {data.daily.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-text-primary mb-2">Day by day</h3>
                  <div className="rounded-lg border border-border-default divide-y divide-border-default">
                    {data.daily.map((d) => (
                      <div key={d.date} className="px-3 py-1.5 flex items-center justify-between text-sm">
                        <span className="text-text-secondary">{new Date(d.date).toLocaleDateString("en-IN", { dateStyle: "medium" })}</span>
                        <span className="tabular-nums text-text-primary">{num(d.questions)} questions · {usd(d.cost_cents)} <span className="text-text-muted">({inr(d.cost_cents, rate)})</span></span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h3 className="text-sm font-semibold text-text-primary mb-2">
                  Questions {data.questions_total > data.questions.length && <span className="text-text-muted font-normal">(latest {data.questions.length} of {data.questions_total})</span>}
                </h3>
                {data.questions.length === 0 ? (
                  <div className="text-sm text-text-muted">No questions in this period.</div>
                ) : (
                  <ul className="space-y-2">
                    {data.questions.map((q, i) => (
                      <li key={i} className="rounded-lg border border-border-default bg-bg-secondary px-3 py-2">
                        <div className="text-sm text-text-primary">{q.question}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-text-muted">
                          <span>{when(q.asked_at)}</span>
                          <span className="text-text-secondary font-medium">{usd(q.cost_cents)} · {inr(q.cost_cents, rate)}</span>
                          <span>{q.steps} data step{q.steps === 1 ? "" : "s"}</span>
                          {q.rating === 1 && <span className="text-emerald-400">👍 rated good</span>}
                          {q.rating === -1 && <span className="text-rose-400">👎 rated wrong</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
